import {
  normalizeId,
  organizationNameOf,
  parseStoredProject,
  planSwitch,
  sameId,
  type OrgWorkspace,
  type WorkspaceService,
} from '@vnetwork/margarita-shell/iam';

/**
 * C9: the note on a page that always uses the home organization (Organizations, Teams, Access, Billing, ...) while the
 * person works in a project of another organization. `stored` is the usable choice (`useWorkingProject().project`, null
 * for All projects, a revoked or unchecked one). A foreign workspace is chosen when it is settled and its organization is
 * not the home one (the test `landingView` uses); the home organization must be known. Never on a page inside a
 * Compute or Kubernetes workspace or on a service landing (`onWorkspaceRoute`), never once dismissed. The note names both
 * organizations or it is not shown: a name the lists did not give is never replaced by a generic phrase.
 */
export function homeOrgNote(input: {
  stored: { organizationId: string } | null;
  settled: boolean;
  home: string | undefined;
  homeName: string | undefined;
  foreignName: string | undefined;
  onWorkspaceRoute: boolean;
  dismissed: boolean;
}): string | null {
  const { stored, settled, home, homeName, foreignName, onWorkspaceRoute, dismissed } = input;
  if (!stored || !settled || !normalizeId(home) || sameId(stored.organizationId, home)) return null;
  if (onWorkspaceRoute || dismissed) return null;
  const homeLabel = normalizeId(homeName);
  const foreignLabel = normalizeId(foreignName);
  if (!homeLabel || !foreignLabel) return null;
  return `This page is about ${homeLabel}, not ${foreignLabel}.`;
}

/** sessionStorage key of the organizations whose note the person closed: a JSON array of organization ids. */
export const HOME_NOTE_DISMISSED_KEY = 'margarita.homeOrgNote.dismissed';

function dismissedIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Whether the note for this foreign organization was closed (the next foreign organization gets its own note). */
export function isNoteDismissed(raw: string | null | undefined, organizationId: string): boolean {
  return dismissedIds(raw).some((id) => sameId(id, organizationId));
}

/** The stored text after closing the note for `organizationId`. */
export function withNoteDismissed(raw: string | null | undefined, organizationId: string): string {
  const ids = dismissedIds(raw);
  return JSON.stringify(ids.some((id) => sameId(id, organizationId)) ? ids : [...ids, normalizeId(organizationId)]);
}

/**
 * The name of a workspace page: the service's own navigation label when it has one (`quotas` is `Quotas & Usage`),
 * otherwise the slug read as words (`some-page` is `Some page`); '' is the dashboard.
 */
export function workspacePageLabel(page: string, serviceLabel?: string): string {
  if (serviceLabel) return serviceLabel;
  const words = page.split('-').filter(Boolean);
  if (words.length === 0) return 'Dashboard';
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
}

/**
 * D2: the document title while another organization's workspace is open, `<page> · <Organization name>`; null for the
 * home workspace and for any page outside a workspace (they keep the title they have).
 */
export function workspaceDocumentTitle(input: {
  home: string | undefined;
  workspaceOrganizationId: string | undefined;
  page: string;
  /** The label the service gives `page` (`WorkspaceService.pageLabel`). */
  pageLabel?: string;
  organizationName: string | undefined;
}): string | null {
  const { home, workspaceOrganizationId, page, pageLabel, organizationName } = input;
  if (!isForeignWorkspace(home, workspaceOrganizationId)) return null;
  return `${workspacePageLabel(page, pageLabel)} · ${organizationLabel(workspaceOrganizationId, organizationName)}`;
}

/** A workspace of another organization: both ids are known and differ. */
function isForeignWorkspace(home: string | undefined, workspaceOrganizationId: string | undefined): boolean {
  return !!normalizeId(home) && !!normalizeId(workspaceOrganizationId) && !sameId(home, workspaceOrganizationId);
}

function organizationLabel(organizationId: string | undefined, name: string | undefined): string {
  return normalizeId(name) || `Organization ${normalizeId(organizationId)}`;
}

/**
 * The home organization's name: the one the shared-projects list gives for it (that list may hold no home entry), else
 * the name of the home workspace of any service.
 */
export function homeOrganizationName(input: {
  projects: readonly { customer_id: string; org_name: string }[];
  workspaces: readonly Pick<OrgWorkspace, 'home' | 'organizationId' | 'organizationName'>[];
  home: string | undefined;
}): string | undefined {
  const { projects, workspaces, home } = input;
  const own = workspaces.find((w) => w.home && sameId(w.organizationId, home) && normalizeId(w.organizationName));
  return organizationNameOf(home, projects, own?.organizationName);
}

/**
 * The location a workspace is read from: Compute keeps the organization in the path, Kubernetes in the search
 * (`?organization_id=`), so both are needed.
 */
export function workspaceLocation(pathname: string, search: string): string {
  return pathname + search;
}

/**
 * The landings the home-organization note never shows on: each adapter's `landing` (the root of its workspace pages) and the
 * service category's own landing page, which for Kubernetes is a different path.
 */
export function serviceLandings(
  services: readonly { type: string; landing: string }[],
  categoryLanding: (type: string) => string | undefined,
): string[] {
  const all = services.flatMap((s) => [s.landing, categoryLanding(s.type)]);
  return [...new Set(all.filter((l): l is string => !!l))];
}

/** A page that is inside a service's workspace or is the landing of one: the home-organization note never shows there. */
export function isServicePage(input: { pathname: string; insideWorkspace: boolean; landings: readonly string[] }): boolean {
  const { pathname, insideWorkspace, landings } = input;
  return insideWorkspace || landings.some((l) => pathname === l || pathname.startsWith(`${l}/`));
}

/**
 * Pages of the home organization's console that talk about other organizations by design (Shared with you): the note
 * "This page is about <home>, not <other>" would contradict them. `organizationsBase` is the organizations console's path.
 */
export function homeNoteFreePages(organizationsBase: string): string[] {
  return [`${organizationsBase}/shared`];
}

type NoteStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** The stored dismissals; blocked or missing storage reads as none (the note comes back on the next page load). */
export function readNoteDismissals(storage: NoteStorage | undefined): string | null {
  try {
    return storage?.getItem(HOME_NOTE_DISMISSED_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Closes the note for `organizationId`: returns the new stored text, written when the storage lets it. */
export function dismissNote(storage: NoteStorage | undefined, raw: string | null, organizationId: string): string {
  const next = withNoteDismissed(raw, organizationId);
  try {
    storage?.setItem(HOME_NOTE_DISMISSED_KEY, next);
  } catch {
    /* storage blocked: closed until the page reloads */
  }
  return next;
}

/**
 * Where the Reload of the other-tab notice goes. A reload keeps the URL, which would leave a foreign workspace page open
 * after the other tab chose a project elsewhere; so it goes to the page of the new choice's workspace (`planSwitch`:
 * same page type, dashboard or list as needed). null = nothing to follow (no usable stored choice, or no workspace of
 * the open service for that organization): then a plain reload is all there is.
 */
export function tabFollowTarget(input: {
  registry: readonly WorkspaceService[];
  loc: string;
  storedRaw: string | null;
  workspaces: readonly OrgWorkspace[];
}): string | null {
  const next = parseStoredProject(input.storedRaw);
  if (!next) return null;
  const plan = planSwitch({ registry: input.registry, loc: input.loc, choice: { organizationId: next.organizationId }, workspaces: input.workspaces });
  return plan.kind === 'navigate' ? plan.to : null;
}
